// Merged issues in the console (D102): the issues list merges the selected issues into one, after
// a confirmation that says which issue is kept (the one with the most users, unless another is
// picked) and that the others' states are dropped; the issue page lists the fingerprints merged
// into it, each with Unmerge. Issue titles and fingerprints are the app's own text or ids: they
// are only ever rendered as text, never translated.
import { type FormEvent, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { useMergeIssues, useUnmergeIssue } from '../api/queries'
import type { IssueMergeResult, IssueSummary } from '../api/types'
import { type Translator, useI18n } from '../i18n'
import { shortId } from '../lib/format'
import { issueTitle } from '../lib/issues'
import { Section } from './d'
import { stateErrorMessage } from './IssueStates'

/** Issues one merge takes besides the one kept (the API's MAX_MERGE_ISSUES). */
export const MAX_MERGE_ISSUES = 50

/** The issue a merge keeps unless another is picked: the most users, then occurrences. */
export function defaultTarget(issues: readonly IssueSummary[]): IssueSummary | undefined {
  return [...issues].sort(
    (a, b) => b.users - a.users || b.occurrences - a.occurrences || a.id.localeCompare(b.id),
  )[0]
}

/** Whether an issue has a state a merge into another drops: not unresolved, or assigned. */
export function hasState(issue: IssueSummary): boolean {
  const s = issue.state
  return !!s && (s.status !== 'unresolved' || !!s.assignee_id)
}

/** Why a merge or an unmerge did not go through. */
export function mergeErrorMessage(t: Translator['t'], error: unknown): string {
  if (isApiError(error, 409)) return t('issues.merge.error.cap')
  if (isApiError(error, 404)) return t('issues.merge.error.notFound')
  if (isApiError(error, 403)) return t('issues.merge.error.forbidden')
  return stateErrorMessage(t, error)
}

/** The issues list's confirmation: which issue to keep, and what the merge does. */
export function MergeIssues({
  projectId,
  issues,
  onCancel,
  onMerged,
}: {
  projectId: string
  issues: IssueSummary[]
  onCancel: () => void
  onMerged: (result: IssueMergeResult) => void
}) {
  const { t } = useI18n()
  const merge = useMergeIssues(projectId)
  const [picked, setPicked] = useState<string>()
  const select = useId()
  const target = issues.find((i) => i.id === picked) ?? defaultTarget(issues)
  const others = issues.filter((i) => i.id !== target?.id)
  const dropped = others.filter(hasState).length
  const tooMany = others.length > MAX_MERGE_ISSUES

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!target || others.length === 0 || tooMany || merge.isPending) return
    merge.mutate({ into: target.id, issueIds: others.map((i) => i.id) }, { onSuccess: onMerged })
  }
  return (
    <form className="win" onSubmit={submit} aria-labelledby={`${select}-title`}>
      <p id={`${select}-title`} className="win-bar">
        {t('issues.merge.title', { count: issues.length })}
      </p>
      <div style={{ display: 'grid', gap: 10, padding: '12px 14px' }}>
        <div className="field">
          <label htmlFor={select}>{t('issues.merge.keep')}</label>
          <select
            id={select}
            className="select"
            value={target?.id ?? ''}
            onChange={(e) => setPicked(e.target.value)}
          >
            {issues.map((i) => (
              <option key={i.id} value={i.id} translate="no">
                {`${issueTitle(i)} · ${shortId(i.id, 8, 4)}`}
              </option>
            ))}
          </select>
        </div>
        <p className="note">{t('issues.merge.body')}</p>
        {dropped > 0 ? (
          <p className="notice">{t('issues.merge.dropped', { count: dropped })}</p>
        ) : null}
        {tooMany ? (
          <p className="notice error">{t('issues.merge.tooMany', { max: MAX_MERGE_ISSUES + 1 })}</p>
        ) : null}
        {merge.isError ? (
          <p role="alert" className="notice error">
            {mergeErrorMessage(t, merge.error)}
          </p>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="submit"
            className="btn sm solid"
            disabled={!target || others.length === 0 || tooMany || merge.isPending}
          >
            {t('issues.merge.confirm', { count: issues.length })}
          </button>
          <button type="button" className="btn sm ghost" onClick={onCancel}>
            {t('issue.actions.cancel')}
          </button>
        </div>
      </div>
    </form>
  )
}

/**
 * The issue page's merged fingerprints: each one merged into this issue, with Unmerge (not in
 * the sandbox), and a line when the page was opened by one of them.
 */
export function MergedIssues({
  projectId,
  issueId,
  fingerprints,
  mergedFrom,
  readOnly,
}: {
  projectId: string
  /** The issue's id: its primary fingerprint. */
  issueId: string
  /** Its fingerprints, its id first (the API's order). */
  fingerprints: readonly string[]
  /** The fingerprint the page was opened by, when it was merged into this issue. */
  mergedFrom?: string | undefined
  readOnly: boolean
}) {
  const { t } = useI18n()
  const unmerge = useUnmergeIssue(projectId)
  const [done, setDone] = useState('')
  const merged = fingerprints.filter((f) => f !== issueId)
  if (merged.length === 0 && !mergedFrom && !done) return null
  return (
    <Section
      title={t('issue.merged.title')}
      meta={t('issue.merged.sub', { count: merged.length })}
      minor
    >
      {mergedFrom ? (
        <p className="note">
          {t('issue.merged.openedBy')} <span translate="no">{shortId(mergedFrom, 8, 4)}</span>
        </p>
      ) : null}
      <ul className="rl">
        {merged.map((f) => (
          <li key={f}>
            <span className="mono anywhere" style={{ fontSize: 12.5 }} translate="no">
              {f}
            </span>
            {readOnly ? (
              <span />
            ) : (
              <button
                type="button"
                className="btn ghost sm"
                aria-label={t('issue.merged.unmergeOne', { id: shortId(f, 8, 4) })}
                disabled={unmerge.isPending}
                onClick={() => {
                  setDone('')
                  unmerge.mutate(
                    { issueId, fingerprint: f },
                    {
                      onSuccess: () => setDone(t('issue.merged.done', { id: shortId(f, 8, 4) })),
                    },
                  )
                }}
              >
                {t('issue.merged.unmerge')}
              </button>
            )}
          </li>
        ))}
      </ul>
      <p role="status" className="note">
        {done}
      </p>
      {unmerge.isError ? (
        <p role="alert" className="notice error">
          {mergeErrorMessage(t, unmerge.error)}
        </p>
      ) : null}
    </Section>
  )
}
